import { useLayoutEffect, useRef, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import type { FeedState } from "../model/feedReducer";
import { AssistantMessageItem } from "./AssistantMessageItem";

export interface AssistantFeedProps {
  state: FeedState;
  onOption: (option: string) => void;
  onExecute: (planId: string, confirmedSteps: number[]) => void;
  onCancel: (planId: string) => Promise<unknown> | undefined;
}

/** Близость к низу, при которой лента продолжает ехать за ответом. */
const STICK_THRESHOLD = 80;

const scrollParent = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

/**
 * Лента помощника (п. 9 спеки). Прокрутка идёт за ответом, пока сотрудник
 * у низа ленты; если он отмотал вверх почитать, поток его не дёргает.
 * Новая реплика всегда прокручивает вниз.
 */
export const AssistantFeed: FC<AssistantFeedProps> = ({
  state,
  onOption,
  onExecute,
  onCancel,
}) => {
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const count = useRef(0);

  // Контейнер прокрутки (тело панели) один на всё время жизни ленты:
  // ищем его один раз при монтировании, а не на каждый кусок потока.
  const scroller = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = scrollParent(root.current);
    scroller.current = el;
    if (!el) return;
    const onScroll = () => {
      stick.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const el = scroller.current;
    const added = state.items.length !== count.current;
    count.current = state.items.length;
    if (!el || !(added || stick.current)) return;
    el.scrollTop = el.scrollHeight;
    stick.current = true;
  }, [state.items]);

  if (!state.items.length) {
    return (
      <Typography ref={root} variant="body2" color="text.secondary">
        {t("partnerAssistant.empty")}
      </Typography>
    );
  }
  const last = state.items.length - 1;
  return (
    <Stack ref={root} gap={2}>
      {state.items.map((item, index) => (
        <AssistantMessageItem
          key={item.id}
          item={item}
          isLast={index === last}
          busy={state.busy}
          onOption={onOption}
          onExecute={onExecute}
          onCancel={onCancel}
        />
      ))}
    </Stack>
  );
};
